import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { getStoredLanguage, setStoredLanguage, LanguageCode, isValidLanguageCode, isRTL } from "@/lib/languages";
/** Bundled so `t(key)` never flashes raw keys before `/translations/en.json` finishes loading. */
import bundledEnglish from "../../public/translations/en.json";

// Extend window type for global translation cache
declare global {
  interface Window {
    __translationCache?: Map<string, string>;
  }
}

// In-memory cache for static translations (loaded from JSON files)
const staticTranslationsCache = new Map<string, Record<string, string>>();

// In-memory cache for dynamic translations (from API - checklist items, etc.)
// Exposed to window.__translationCache for debugging and persistence
const dynamicSessionCache = new Map<string, string>();
if (typeof window !== 'undefined') {
  window.__translationCache = dynamicSessionCache;
}
let currentDynamicCacheLanguage: string | null = null;

// Pending translations queue for batching dynamic requests
let pendingTexts: Set<string> = new Set();
let batchTimeout: ReturnType<typeof setTimeout> | null = null;
let batchResolvers: Array<() => void> = [];

// English: always available synchronously from bundle; fetch may refresh at runtime.
const BUNDLED_ENGLISH: Record<string, string> = bundledEnglish as Record<string, string>;
let englishTranslations: Record<string, string> = { ...BUNDLED_ENGLISH };
let isEnglishLoaded = true;
staticTranslationsCache.set("en", BUNDLED_ENGLISH);

// Load static translations from JSON file (non-blocking)
const loadStaticTranslations = async (lang: string): Promise<Record<string, string>> => {
  // Return from cache immediately if available
  if (staticTranslationsCache.has(lang)) {
    return staticTranslationsCache.get(lang)!;
  }

  // Map language codes to file names (handle variants)
  const langMap: Record<string, string> = {
    'zh-CN': 'zh',
    'zh-TW': 'zh',
  };
  const fileName = langMap[lang] || lang;

  try {
    const response = await fetch(`/translations/${fileName}.json`);
    if (response.ok) {
      const translations = await response.json();
      staticTranslationsCache.set(lang, translations);
      return translations;
    }
  } catch (error) {
    console.warn(`Failed to load translations for ${lang}`);
  }

  // Fallback to English if available
  return staticTranslationsCache.get('en') || englishTranslations;
};

// Refresh English from CDN in background (deployed JSON can override bundle without app rebuild).
(async () => {
  try {
    const response = await fetch("/translations/en.json");
    if (response.ok) {
      const fetched = (await response.json()) as Record<string, string>;
      englishTranslations = { ...BUNDLED_ENGLISH, ...fetched };
      staticTranslationsCache.set("en", englishTranslations);
      isEnglishLoaded = true;
    }
  } catch (error) {
    console.error("Failed to refresh English translations", error);
  }
})();

// Preload stored language on module load for instant display
const storedLang = getStoredLanguage();
if (storedLang !== 'en') {
  loadStaticTranslations(storedLang).catch(() => {});
}

export const useTranslation = () => {
  const { profile } = useAuth();
  
  // Priority: profile.language > localStorage > "en"
  // Use stored language immediately (no waiting for profile fetch)
  const storedLang = getStoredLanguage();
  const profileLang = (profile as any)?.language;
  const lang: LanguageCode = isValidLanguageCode(profileLang) 
    ? profileLang 
    : storedLang;
  
  // Initialize with cached translations or English fallback (INSTANT - no loading state)
  const [staticTranslations, setStaticTranslations] = useState<Record<string, string>>(() => {
    return staticTranslationsCache.get(lang) || 
           staticTranslationsCache.get(storedLang) || 
           staticTranslationsCache.get('en') || 
           englishTranslations;
  });
  
  const [, forceUpdate] = useState(0);
  const mountedRef = useRef(true);
  const currentLangRef = useRef(lang);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Sync localStorage when profile language changes (background, non-blocking)
  useEffect(() => {
    if (profileLang && isValidLanguageCode(profileLang) && profileLang !== storedLang) {
      setStoredLanguage(profileLang);
    }
  }, [profileLang, storedLang]);

  // Load static translations when language changes (non-blocking)
  useEffect(() => {
    currentLangRef.current = lang;
    
    // If already cached, update immediately
    if (staticTranslationsCache.has(lang)) {
      setStaticTranslations(staticTranslationsCache.get(lang)!);
      return;
    }

    // Load in background, update silently when ready
    loadStaticTranslations(lang).then(t => {
      if (mountedRef.current && currentLangRef.current === lang) {
        setStaticTranslations(t);
      }
    });

    // Clear dynamic cache when language changes
    if (currentDynamicCacheLanguage !== lang) {
      dynamicSessionCache.clear();
      currentDynamicCacheLanguage = lang;
    }
  }, [lang]);

  // Static translation function - INSTANT, no API calls, no loading
  const t = useCallback((key: string): string => {
    return staticTranslations[key] || englishTranslations[key] || key;
  }, [staticTranslations]);

  // Process batch of dynamic translations (for checklist items, etc.)
  const processBatch = useCallback(async () => {
    if (pendingTexts.size === 0) return;
    
    const textsToTranslate = Array.from(pendingTexts);
    pendingTexts.clear();
    const resolvers = [...batchResolvers];
    batchResolvers = [];

    const currentLang = currentLangRef.current;

    if (currentLang === 'en') {
      textsToTranslate.forEach(text => dynamicSessionCache.set(text, text));
      resolvers.forEach(r => r());
      return;
    }

    try {
      // First check Supabase cache for existing translations
      const { data: cached } = await supabase
        .from('translations_cache')
        .select('source_text, translated_text')
        .eq('language', currentLang)
        .in('source_text', textsToTranslate);

      const uncachedTexts: string[] = [];
      
      if (cached) {
        cached.forEach((c: any) => {
          dynamicSessionCache.set(c.source_text, c.translated_text);
        });
        const cachedSources = new Set(cached.map((c: any) => c.source_text));
        textsToTranslate.forEach(t => {
          if (!cachedSources.has(t)) {
            uncachedTexts.push(t);
          }
        });
      } else {
        uncachedTexts.push(...textsToTranslate);
      }

      // Call OpenAI edge function only for uncached dynamic content
      if (uncachedTexts.length > 0) {
        const { data, error } = await supabase.functions.invoke('translate-text', {
          body: { texts: uncachedTexts, targetLanguage: currentLang }
        });

        if (!error && data?.translations) {
          Object.entries(data.translations).forEach(([source, translated]) => {
            dynamicSessionCache.set(source, translated as string);
          });
        } else {
          // Fallback: use original texts
          uncachedTexts.forEach(t => dynamicSessionCache.set(t, t));
        }
      }
    } catch (error) {
      console.error('Translation batch error:', error);
      // Fallback: use original texts
      textsToTranslate.forEach(t => {
        if (!dynamicSessionCache.has(t)) {
          dynamicSessionCache.set(t, t);
        }
      });
    }

    resolvers.forEach(r => r());
  }, []);

  // Dynamic translation function for checklist items, room names, inventory, notes
  // Returns original text immediately, updates silently when translation arrives
  const tDynamic = useCallback((text: string): string => {
    if (!text) return text;
    
    const currentLang = currentLangRef.current;
    if (currentLang === 'en') return text;

    // Return cached translation if available (instant)
    if (dynamicSessionCache.has(text)) {
      return dynamicSessionCache.get(text)!;
    }

    // Add to pending batch for background processing
    pendingTexts.add(text);
    
    // Schedule batch processing (debounced)
    if (!batchTimeout) {
      batchTimeout = setTimeout(() => {
        batchTimeout = null;
        processBatch().then(() => {
          if (mountedRef.current) {
            forceUpdate(n => n + 1);
          }
        });
      }, 50);
    }

    // Return original text immediately while translation loads in background
    return text;
  }, [processBatch]);

  // Change language instantly (updates localStorage and loads static JSON)
  const changeLanguage = useCallback((newLang: LanguageCode) => {
    setStoredLanguage(newLang);
    currentLangRef.current = newLang;
    
    // Update immediately if cached
    if (staticTranslationsCache.has(newLang)) {
      setStaticTranslations(staticTranslationsCache.get(newLang)!);
    } else {
      // Load and update silently
      loadStaticTranslations(newLang).then(t => {
        if (mountedRef.current && currentLangRef.current === newLang) {
          setStaticTranslations(t);
        }
      });
    }

    // Clear dynamic cache for new language
    dynamicSessionCache.clear();
    currentDynamicCacheLanguage = newLang;
  }, []);

  // Check if current language is RTL
  const isRtl = useMemo(() => isRTL(lang), [lang]);

  // Clear dynamic cache (useful when switching languages)
  const clearDynamicCache = useCallback(() => {
    dynamicSessionCache.clear();
  }, []);

  return { 
    t,              // Static translation function (instant, from JSON files)
    tDynamic,       // Dynamic translation function (for checklist items, uses OpenAI)
    lang,           // Current language code
    changeLanguage, // Function to change language instantly
    isRtl,          // Whether current language is RTL
    clearDynamicCache, // Clear dynamic translation cache
  };
};

// Preload translations for common languages (call on app init for faster switching)
export const preloadTranslations = async (languages: string[] = ['en', 'es', 'fr', 'pt', 'de']) => {
  await Promise.all(languages.map(lang => loadStaticTranslations(lang)));
};
